import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Globe, Loader2, ShieldAlert, AlertTriangle, Sparkles,
  Building2, Layers, Workflow, Eye, EyeOff, Target,
  Share2, Copy, Check, X, LayoutDashboard,
} from "lucide-react";
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const certColor = {
  Gold:   "text-amber-300 border-amber-300 bg-amber-300/10",
  Silver: "text-slate-200 border-slate-300 bg-slate-300/10",
  Bronze: "text-orange-300 border-orange-300 bg-orange-300/10",
  Watch:  "text-red-300  border-red-400  bg-red-400/10",
};

const sevColor = {
  High:   "text-red-300  border-red-400  bg-red-400/10",
  Medium: "text-amber-300 border-amber-400 bg-amber-400/10",
  Low:    "text-emerald-300 border-emerald-400 bg-emerald-400/10",
};

// ---------- small presentational helpers ----------

const SectionTitle = ({ icon: Icon, title, sub }) => (
  <div className="mb-3 flex items-center gap-2">
    {Icon && <Icon size={16} className="text-electric" />}
    <h2 className="font-display text-xl text-cream">{title}</h2>
    {sub && <span className="text-xs text-cream-dim">· {sub}</span>}
  </div>
);

const PillarTile = ({ label, value }) => {
  const v = Number(value || 0);
  return (
    <div className="glass p-4 rounded-md text-center" data-testid={`crawl-pillar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-1">{label}</div>
      <div className="font-display text-3xl text-electric">{v.toFixed(1)}</div>
      <div className="mt-2 h-1.5 rounded bg-navy-deep overflow-hidden">
        <div className="h-full bg-electric" style={{ width: `${Math.min(100, v)}%` }} />
      </div>
    </div>
  );
};

const ChetAxisRow = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-electric/10">
    <span className="text-sm text-cream capitalize">{String(k).replace(/_/g, " ")}</span>
    <span className="text-sm text-electric font-mono">{typeof v === "number" ? v.toFixed(1) : String(v)}</span>
  </div>
);

const RiskCard = ({ icon: Icon, color, item }) => (
  <div className={`glass p-4 rounded-md border-l-4 ${color}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} />
      <div className="text-[10px] uppercase tracking-[0.2em]">{item.id || ""}</div>
    </div>
    <div className="font-display text-base text-cream">{item.title}</div>
    {item.evidence && <div className="text-xs text-cream-dim mt-1">{item.evidence}</div>}
    {item.cause && <div className="text-xs text-cream-dim mt-1 italic">{item.cause}</div>}
  </div>
);

const ActionCard = ({ a }) => (
  <div className="glass p-3 rounded-md">
    <div className="flex items-center justify-between gap-2">
      <div className="text-sm text-cream">{a.title}</div>
      <span className="text-xs text-electric font-mono">{a.cost_band}</span>
    </div>
    <div className="text-xs text-cream-dim mt-1">{a.rationale}</div>
    <div className="text-[11px] text-cream-dim mt-2 flex items-center gap-3">
      <span><strong className="text-cream">Owner:</strong> {a.suggested_owner}</span>
      <span><strong className="text-cream">Cost:</strong> {fmtMoney(a.cost_estimate)}</span>
      <span><strong className="text-cream">Risk ↓:</strong> {a.estimated_risk_reduction_pct}%</span>
    </div>
  </div>
);

const DecisionOption = ({ opt, recommended }) => (
  <div className={`glass p-3 rounded-md ${recommended ? "border border-electric" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="font-display text-base text-cream">
        Option {opt.letter} · {opt.kind}
      </div>
      {recommended && (
        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border border-electric text-electric">
          Recommended
        </span>
      )}
    </div>
    <div className="text-sm text-cream mt-1">{opt.title}</div>
    <div className="grid grid-cols-3 gap-2 mt-2 text-[11px] text-cream-dim">
      <div><strong className="text-cream">Cost:</strong> {fmtMoney(opt.estimated_cost)}</div>
      <div><strong className="text-cream">Risk ↓:</strong> {opt.estimated_risk_reduction_pct}%</div>
      <div><strong className="text-cream">Timeline:</strong> {opt.timeline}</div>
    </div>
    <div className="grid grid-cols-2 gap-3 mt-2 text-[11px]">
      <div>
        <div className="text-emerald-300 mb-1">Pros</div>
        <ul className="text-cream-dim list-disc pl-4 space-y-0.5">
          {(opt.pros || []).map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </div>
      <div>
        <div className="text-red-300 mb-1">Cons</div>
        <ul className="text-cream-dim list-disc pl-4 space-y-0.5">
          {(opt.cons || []).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </div>
    </div>
  </div>
);

// ---------- main page ----------

const AuditCrawler = () => {
  const [url, setUrl] = useState("");
  const [scanMode, setScanMode] = useState("quick_scan");
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);   // { share_url, share_token }
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [perms, setPerms] = useState(null);

  const isSalesDemo = perms?.role === "sales_demo";

  useEffect(() => {
    api.get("/auditiq/crawler/modes")
      .then(r => {
        const existing = r.data?.modes || [];
        const marketingModes = [
          { key: "marketing_traction", label: "Full Marketing Traction Audit", description: "SEO + performance + conversion + social + reputation + competitor gap" },
          { key: "seo_audit",          label: "SEO Audit",                     description: "Search visibility, meta tags, schema, backlinks, keyword density" },
          { key: "reputation_audit",   label: "Reputation Audit",              description: "Review ratings, sentiment, response rate, Google Business Profile" },
          { key: "social_audit",       label: "Social Media Audit",            description: "Platform coverage, posting frequency, engagement rates" },
          { key: "website_performance", label: "Website Performance Audit",    description: "Load speed, mobile-friendliness, SSL, broken links, analytics" },
        ];
        setModes([...existing, ...marketingModes]);
      })
      .catch(() => {});
    api.get("/auditiq/industries")
      .then(r => setIndustries(r.data?.industries || []))
      .catch(() => {});
    api.get("/auditiq/my-permissions")
      .then(r => setPerms(r.data))
      .catch(() => {});
  }, []);

  const MARKETING_MODES = new Set([
    "marketing_traction", "seo_audit", "reputation_audit",
    "social_audit", "website_performance",
  ]);

  const submit = async (e) => {
    e.preventDefault();

    // Marketing Traction flow — routes to new engine
    if (MARKETING_MODES.has(scanMode)) {
      if (!url) return toast.error("Enter a website URL");
      if (!/^https?:\/\//i.test(url))
        return toast.error("URL must start with http:// or https://");
      setBusy(true); setResult(null); setShareInfo(null);
      try {
        const r = await api.post("/auditiq/marketing/traction-scan", {
          website_url: url,
          organization_name: orgName || url,
          organization_id: (typeof window !== "undefined" && localStorage.getItem("auditiq_active_org")) || "default",
          industry: industry || "marketing_business",
        }, { timeout: 60_000 });
        setResult({
          is_marketing_scan: true,
          audit_id: r.data.audit_id,
          organization_name: r.data.organization_name,
          website_url: r.data.website_url,
          traction: r.data.traction,
          transparency: r.data.transparency,
          savings: r.data.savings,
          chet: r.data.chet,
          findings: r.data.findings || [],
          signals: r.data.signals || {},
          stats: r.data.stats || {},
        });
        toast.success(`Marketing scan complete — ${r.data.traction?.grade || "Done"} (${r.data.traction?.traction_score || 0}/100)`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Marketing scan failed");
      }
      setBusy(false);
      return;
    }

    // Demo scenario flow removed — crawler now always runs live engines

    if (!url) return toast.error("Enter a website URL");
    if (!/^https?:\/\//i.test(url))
      return toast.error("URL must start with http:// or https://");
    setBusy(true);
    setResult(null);
    setShareInfo(null);
    try {
      const r = await api.post("/auditiq/crawler/scan", {
        url,
        scan_mode: scanMode,
        organization_name: orgName || undefined,
        industry: industry || undefined,
      }, { timeout: 180_000 });
      setResult(r.data);
      toast.success(`Scan complete in ${r.data.duration_seconds}s — ${r.data.pages_crawled} pages.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    }
    setBusy(false);
  };

  const generateShareLink = async () => {
    if (!result?.crawl_id) return;
    setShareBusy(true);
    try {
      const r = await api.post(`/auditiq/crawler/scan/${result.crawl_id}/share`);
      const origin = (typeof window !== "undefined" && window.location?.origin) || "";
      setShareInfo({
        share_token: r.data.share_token,
        share_url: `${origin}${r.data.share_path}`,
      });
      toast.success("Public scorecard link generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate share link");
    }
    setShareBusy(false);
  };

  const revokeShareLink = async () => {
    if (!result?.crawl_id) return;
    setShareBusy(true);
    try {
      await api.delete(`/auditiq/crawler/scan/${result.crawl_id}/share`);
      setShareInfo(null);
      toast.success("Public scorecard link revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke link");
    }
    setShareBusy(false);
  };

  const copyShareLink = async () => {
    if (!shareInfo?.share_url) return;
    try {
      await navigator.clipboard.writeText(shareInfo.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select the URL manually.");
    }
  };

  const cert = result?.five_pillars?.certification_level || "Watch";
  const certCls = certColor[cert] || certColor.Watch;

  const recommendations = result?.recommendations;
  const decisions = result?.decision_options || [];
  const hawkins = result?.hawkins;
  const standards = result?.standards;
  const analytics = result?.analytics;

  const allActions = useMemo(() => {
    if (!recommendations) return [];
    return [
      ...(recommendations.immediate_actions || []).map(a => ({ ...a, when: "Immediate" })),
      ...(recommendations.thirty_day_actions || []).map(a => ({ ...a, when: "30-day" })),
      ...(recommendations.ninety_day_actions || []).map(a => ({ ...a, when: "90-day" })),
      ...(recommendations.long_term_actions || []).map(a => ({ ...a, when: "Long-term" })),
    ];
  }, [recommendations]);

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ · AI Website Crawler</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 text-cream">
            Crawl any organization's public web ecosystem
          </h1>
          <p className="text-sm text-cream-dim mt-1 max-w-3xl">
            Enter a public URL and AuditIQ runs a robots-respecting crawl, then analyzes it through the
            Chet Protocol, Five Pillars, Hawkins 3+3+1 Black Hole framework, and standards mapping —
            producing a full executive decision report.
          </p>
        </div>

        {/* INPUT FORM */}
        <form onSubmit={submit} className="glass p-6 grid lg:grid-cols-12 gap-4 max-w-5xl">
          <div className="lg:col-span-6">
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Website URL</label>
            <div className="mt-1 flex items-center gap-2">
              <Globe size={14} className="text-electric" />
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.cityname.gov"
                className="bg-navy-deep border-electric/40 text-cream"
                data-testid="crawler-url-input"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Scan mode</label>
            <Select value={scanMode} onValueChange={setScanMode}>
              <SelectTrigger className="mt-1 bg-navy-deep border-electric/40 text-cream"
                             data-testid="crawler-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-electric/40 text-cream">
                {modes.map(m => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-3">
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Industry</label>
            <Select value={industry || "auto"} onValueChange={(v) => setIndustry(v === "auto" ? "" : v)}>
              <SelectTrigger className="mt-1 bg-navy-deep border-electric/40 text-cream"
                             data-testid="crawler-industry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-electric/40 text-cream">
                <SelectItem value="auto">Auto / Generic (equal weights)</SelectItem>
                {industries.map(i => (
                  <SelectItem key={i.key} value={i.key}>{i.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-6">
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Org label (optional)</label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)}
              placeholder="City of …"
              className="mt-1 bg-navy-deep border-electric/40 text-cream"
              data-testid="crawler-org-input" />
          </div>
          <div className="lg:col-span-12 flex items-center justify-between">
            <div className="text-[11px] text-cream-dim">
              {modes.find(m => m.key === scanMode)?.description || ""}
            </div>
            <Button type="submit" disabled={busy} className="btn-gold h-11 px-6"
                    data-testid="crawler-submit-btn">
              {busy
                ? <><Loader2 size={14} className="mr-2 animate-spin" /> Crawling…</>
                : <><Sparkles size={14} className="mr-2" /> Run Scan</>}
            </Button>
          </div>
        </form>

        {/* MARKETING TRACTION RESULTS */}
        {result?.is_marketing_scan && (
          <div className="space-y-6" data-testid="marketing-result">
            <div className="glass p-6" data-testid="marketing-header">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Marketing & Traction Intelligence</div>
                  <h2 className="font-display text-2xl text-cream mt-1">{result.organization_name}</h2>
                  <div className="text-xs text-cream-dim mt-1 font-mono">{result.website_url}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-[0.2em] border ${
                    (result.traction?.traction_score || 0) >= 70
                      ? "bg-gold/15 text-gold border-gold"
                      : (result.traction?.traction_score || 0) >= 55
                      ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/40"
                      : "bg-red-500/15 text-red-300 border-red-500/40"
                  }`} data-testid="marketing-traction-grade">
                    {result.traction?.grade} · {result.traction?.traction_score}/100
                  </span>
                </div>
              </div>
            </div>

            {/* 4-up KPI row */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="glass p-4" data-testid="marketing-kpi-findings">
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Findings</div>
                <div className="metric text-3xl text-gold mt-1">{result.stats?.totalFindings || 0}</div>
                <div className="text-[11px] text-cream-dim mt-1">{result.stats?.highSeverityCount || 0} high severity</div>
              </div>
              <div className="glass p-4" data-testid="marketing-kpi-modules">
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Active Modules</div>
                <div className="metric text-3xl text-gold mt-1">{result.stats?.activeModules || 0}</div>
                <div className="text-[11px] text-cream-dim mt-1">protocols with findings</div>
              </div>
              <div className="glass p-4" data-testid="marketing-kpi-pillars">
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Pillar Overall</div>
                <div className="metric text-3xl text-gold mt-1">{result.transparency?.overall_score || "—"}</div>
                <div className="text-[11px] text-cream-dim mt-1">{result.transparency?.certification_level || ""}</div>
              </div>
              <div className="glass p-4" data-testid="marketing-kpi-revenue">
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Revenue Opportunity</div>
                <div className="metric text-3xl text-gold mt-1">
                  ${((result.traction?.annual_revenue_opportunity || 0) / 1000).toFixed(0)}k
                </div>
                <div className="text-[11px] text-cream-dim mt-1">estimated annual</div>
              </div>
            </div>

            {/* Chet executive summary */}
            <div className="glass p-6" data-testid="marketing-chet">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Chet Executive Intelligence</div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[color:var(--auditiq-critical)]/15 text-[color:var(--auditiq-critical)] border border-[color:var(--auditiq-critical)]/40">
                  {result.chet?.priorityLevel || "Standard"} Priority
                </span>
              </div>
              <p className="text-cream-dim mt-3 leading-relaxed text-sm">{result.chet?.executiveSummary}</p>
              {result.chet?.topRisks && (
                <div className="mt-4 pt-4 border-t border-gold-dim/20">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Top Risks</div>
                  <pre className="text-xs text-cream-dim whitespace-pre-wrap font-sans">{result.chet.topRisks}</pre>
                </div>
              )}
              {result.chet?.recommendedActions && (
                <div className="mt-4 pt-4 border-t border-gold-dim/20">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Recommended Actions</div>
                  <pre className="text-xs text-cream-dim whitespace-pre-wrap font-sans">{result.chet.recommendedActions}</pre>
                </div>
              )}
            </div>

            {/* Protocol panels */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: "seo-health", label: "SEO Health", signals: [
                  { ok: result.signals?.has_meta_title, label: "Page Title Tags" },
                  { ok: result.signals?.has_meta_description, label: "Meta Descriptions" },
                  { ok: result.signals?.has_schema_markup, label: "Schema Markup" },
                  { ok: result.signals?.has_sitemap, label: "XML Sitemap" },
                  { ok: result.signals?.has_ssl, label: "SSL Certificate" },
                  { ok: result.signals?.is_mobile_friendly, label: "Mobile Responsive" },
                ] },
                { key: "conversion", label: "Conversion Readiness", signals: [
                  { ok: result.signals?.has_cta, label: "Call to Action" },
                  { ok: result.signals?.has_contact_form, label: "Lead Capture Form" },
                  { ok: result.signals?.has_analytics, label: "Analytics Tracking" },
                  { ok: result.signals?.has_conversion_tracking, label: "Conversion Tracking" },
                  { ok: result.signals?.has_email_capture, label: "Email Capture" },
                  { ok: result.signals?.has_privacy_policy, label: "Privacy Policy" },
                ] },
                { key: "social", label: "Social Engagement", signals: [
                  { ok: (result.signals?.active_social_platforms || 0) >= 3, label: `${result.signals?.active_social_platforms || 0} Social Platforms` },
                  { ok: result.signals?.has_google_business_profile, label: "Google Business Profile" },
                  { ok: result.signals?.has_social_links, label: "Social Links Present" },
                ] },
              ].map(panel => (
                <div key={panel.key} className="glass p-5" data-testid={`marketing-panel-${panel.key}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-electric mb-3">{panel.label}</div>
                  <ul className="space-y-2 text-sm">
                    {panel.signals.map((s, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className={s.ok ? "text-[var(--auditiq-success,#5bbf85)]" : "text-[color:var(--auditiq-critical)]"}>
                          {s.ok ? "✓" : "✗"}
                        </span>
                        <span className={s.ok ? "text-cream" : "text-cream-dim"}>{s.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Revenue opportunity disclaimer */}
            <div className="glass p-5 border-l-4 border-gold" data-testid="marketing-revenue-disclaimer">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Estimated Annual Revenue Opportunity</div>
              <div className="font-display text-4xl text-gold">
                ${(result.traction?.annual_revenue_opportunity || 0).toLocaleString()}
              </div>
              <p className="text-[11px] text-cream-dim mt-3 leading-relaxed">
                Based on conversion rate improvement benchmarks — not a guarantee of outcomes.
                Revenue opportunity estimates are based on industry conversion benchmarks and
                are provided for strategic planning purposes only. Actual results depend on
                execution, market conditions, and other factors. AuditIQ marketing intelligence
                is decision-support only.
              </p>
            </div>

            {/* Findings table */}
            {result.findings && result.findings.length > 0 && (
              <div className="glass overflow-x-auto" data-testid="marketing-findings-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-[0.18em] text-cream-dim border-b border-gold-dim/30">
                      <th className="text-left px-4 py-3">Finding</th>
                      <th className="text-left px-4 py-3">Category</th>
                      <th className="text-left px-4 py-3">Severity</th>
                      <th className="text-left px-4 py-3">Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.findings.map((f, i) => (
                      <tr key={i} className="border-b border-gold-dim/10">
                        <td className="px-4 py-3 text-cream">{f.title}</td>
                        <td className="px-4 py-3 text-cream-dim">{f.category}</td>
                        <td className={`px-4 py-3 font-bold ${
                          f.severity === "High" ? "text-[color:var(--auditiq-critical)]"
                            : f.severity === "Medium" ? "text-gold"
                            : "text-cream-dim"}`}>{f.severity}</td>
                        <td className="px-4 py-3 text-cream-dim">{f.recommendation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* RESULTS */}
        {result && !result.is_marketing_scan && (
          <div className="space-y-8" data-testid="crawler-result">
            {/* HEADER */}
            <div className="glass p-5 rounded-md">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-cream-dim">{result.scan_label}</div>
                  <h2 className="font-display text-2xl text-cream mt-1">{result.organization_name}</h2>
                  <div className="text-xs text-cream-dim mt-1">
                    {result.domain} · {result.pages_crawled} pages, {result.pdfs_crawled} PDFs · {result.duration_seconds}s
                    {result.industry_profile?.display_name && (
                      <span className="ml-2 inline-flex items-center gap-1 text-electric">
                        · <Building2 size={11} /> {result.industry_profile.display_name}
                        <span className="text-cream-dim">(industry-weighted)</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] border ${certCls}`}>
                    {cert} · {result.five_pillars.overall_score.toFixed(1)}
                  </span>
                  <span className="px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] border border-red-400 text-red-300 bg-red-400/10">
                    Risk {analytics?.risk_score?.toFixed?.(1) || "—"}
                  </span>
                  <Link
                    to={`/auditiq/dashboard?crawl_id=${result.crawl_id}`}
                    className="btn-gold h-9 px-3 inline-flex items-center gap-2 text-xs rounded-md"
                    data-testid="crawler-open-unified-dashboard"
                  >
                    <LayoutDashboard size={12} /> Open Unified Dashboard
                  </Link>
                </div>
              </div>
              <p className="text-sm text-cream mt-4 leading-relaxed">{result.executive_summary || "—"}</p>

              {/* PUBLIC SCORECARD SHARE */}
              <div className="mt-5 pt-4 border-t border-electric/15">
                {!shareInfo ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-electric">Public Transparency Scorecard</div>
                      <div className="text-xs text-cream-dim mt-1">
                        Generate a polished public-facing page (Five Pillars + Hawkins core) you can share with council, press, or civic groups.
                      </div>
                    </div>
                    <Button onClick={generateShareLink} disabled={shareBusy}
                            className="btn-gold h-10 px-4"
                            data-testid="share-scorecard-btn">
                      {shareBusy
                        ? <><Loader2 size={14} className="mr-2 animate-spin" /> Generating…</>
                        : <><Share2 size={14} className="mr-2" /> Share Public Scorecard</>}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="share-scorecard-panel">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                      <Check size={12} /> Public scorecard live
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input value={shareInfo.share_url} readOnly
                             className="bg-navy-deep border-electric/40 text-cream font-mono text-xs flex-1 min-w-[260px]"
                             data-testid="share-scorecard-url" />
                      <Button onClick={copyShareLink} className="btn-gold h-10 px-3"
                              data-testid="share-scorecard-copy">
                        {copied ? <><Check size={14} className="mr-1" /> Copied</>
                                : <><Copy size={14} className="mr-1" /> Copy</>}
                      </Button>
                      <a href={shareInfo.share_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-electric underline-offset-2 hover:underline px-2"
                         data-testid="share-scorecard-open">
                        Open ↗
                      </a>
                      <Button onClick={revokeShareLink} disabled={shareBusy}
                              variant="ghost"
                              className="h-10 px-3 text-red-300 hover:text-red-200"
                              data-testid="share-scorecard-revoke">
                        <X size={14} className="mr-1" /> Revoke
                      </Button>
                    </div>
                    <div className="text-[11px] text-cream-dim">
                      The scorecard exposes only Five Pillars, Hawkins gravitational core, and headline metrics — no decision-option pricing, no full risk register.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FIVE PILLARS */}
            <div>
              <SectionTitle icon={Layers} title="Five Pillars" sub="0-100, higher is healthier" />
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <PillarTile label="Financial Transparency" value={result.five_pillars.financial_transparency} />
                <PillarTile label="Infrastructure Risk"    value={result.five_pillars.infrastructure_risk} />
                <PillarTile label="Cyber Readiness"        value={result.five_pillars.cyber_readiness} />
                <PillarTile label="Operational Efficiency" value={result.five_pillars.operational_efficiency} />
                <PillarTile label="Public Trust"           value={result.five_pillars.public_trust} />
              </div>
            </div>

            {/* CHET PROTOCOL */}
            <div>
              <SectionTitle icon={Sparkles} title="Chet Protocol · 7 Axis Analysis" />
              <div className="glass p-5 rounded-md grid md:grid-cols-2 gap-6">
                <div>
                  {["clarity","risk","financial_exposure","public_impact","operational_weakness","decision_urgency","recommended_action"].map(k => (
                    <ChetAxisRow key={k} k={k} v={result.chet_protocol?.[k]} />
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Narrative</div>
                  <p className="text-sm text-cream-dim leading-relaxed whitespace-pre-wrap">
                    {result.chet_protocol?.narrative || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* HAWKINS 3+3+1 */}
            {hawkins && (
              <div>
                <SectionTitle icon={Target} title="Hawkins 3+3+1 Black Hole Theory" sub="visible · hidden · gravitational core" />
                <div className="grid lg:grid-cols-3 gap-3 mb-3">
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-1">3 Visible Risks</div>
                    {(hawkins.visible_risks || []).map(r => (
                      <RiskCard key={r.id} icon={Eye} color="border-amber-400" item={r} />
                    ))}
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-1">3 Hidden / Systemic Risks</div>
                    {(hawkins.hidden_risks || []).map(r => (
                      <RiskCard key={r.id} icon={EyeOff} color="border-red-400" item={r} />
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-1">1 Gravitational Core</div>
                    <div className="glass p-4 rounded-md border-2 border-electric">
                      <div className="font-display text-lg text-electric">{hawkins.gravitational_core?.title}</div>
                      <div className="text-sm text-cream-dim mt-2">{hawkins.gravitational_core?.summary}</div>
                      <div className="text-xs text-cream-dim mt-2 italic">{hawkins.gravitational_core?.why_it_matters}</div>
                      <div className="text-xs text-electric mt-2">First move: {hawkins.gravitational_core?.first_move}</div>
                    </div>
                  </div>
                </div>
                <div className="glass p-4 rounded-md">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Escape path</div>
                  <ol className="text-sm text-cream space-y-1 list-decimal pl-5">
                    {(hawkins.escape_path || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              </div>
            )}

            {/* DECISION OPTIONS */}
            {decisions.length > 0 && (
              <div>
                <SectionTitle icon={Workflow} title="Decision Options" sub="three paths per domain · A / B / C" />
                <div className="space-y-4">
                  {decisions.map(d => (
                    <div key={d.domain} className="glass p-4 rounded-md">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="font-display text-lg text-cream">{d.domain}</div>
                          <div className="text-xs text-cream-dim">{d.recommendation_rationale}</div>
                        </div>
                        <span className="text-xs text-electric font-mono">Score {Number(d.domain_score).toFixed(1)}</span>
                      </div>
                      <div className="grid lg:grid-cols-3 gap-3">
                        {d.options.map(opt => (
                          <DecisionOption key={opt.letter} opt={opt}
                            recommended={d.recommended_option === opt.letter} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RECOMMENDATIONS */}
            {recommendations && (
              <div>
                <SectionTitle icon={Sparkles} title="Recommendations"
                  sub={`Confidence: ${recommendations.confidence_level} · est. ${fmtMoney(recommendations.total_estimated_cost)}`} />
                <div className="grid lg:grid-cols-4 gap-3">
                  {[
                    ["Immediate", recommendations.immediate_actions],
                    ["30-day",    recommendations.thirty_day_actions],
                    ["90-day",    recommendations.ninety_day_actions],
                    ["Long-term", recommendations.long_term_actions],
                  ].map(([label, actions]) => (
                    <div key={label}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-electric mb-2">{label}</div>
                      <div className="space-y-2">
                        {(actions || []).map((a, i) => <ActionCard key={i} a={a} />)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ANALYTICS */}
            {analytics && (
              <div>
                <SectionTitle icon={ShieldAlert} title="Analytics" />
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="glass p-4 rounded-md">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Severity flags</div>
                    {(analytics.severity_flags || []).length === 0
                      ? <div className="text-xs text-cream-dim italic">No flags raised by signal scan.</div>
                      : (analytics.severity_flags || []).map((f, i) => (
                          <div key={i} className={`p-2 rounded mb-1 border-l-2 ${sevColor[f.severity] || ""}`}>
                            <div className="text-sm text-cream">{f.title}</div>
                            <div className="text-xs text-cream-dim">{f.domain} · {f.evidence}</div>
                          </div>
                        ))}
                  </div>
                  <div className="glass p-4 rounded-md">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Public-facing weaknesses</div>
                    <ul className="text-sm text-cream-dim list-disc pl-5 space-y-1">
                      {(analytics.public_weaknesses || []).map((w, i) => <li key={i}>{w}</li>)}
                      {(analytics.public_weaknesses || []).length === 0 && <li>No structural weaknesses surfaced.</li>}
                    </ul>
                  </div>
                  <div className="glass p-4 rounded-md">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Department insights</div>
                    {(analytics.department_insights || []).length === 0
                      ? <div className="text-xs text-cream-dim italic">No departments detected from public content.</div>
                      : (
                        <table className="w-full text-sm">
                          <tbody>
                            {analytics.department_insights.map((d, i) => (
                              <tr key={i} className="border-b border-electric/10">
                                <td className="py-1 text-cream">{d.department}</td>
                                <td className="py-1 text-electric font-mono text-right">{d.page_mentions}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  </div>
                  <div className="glass p-4 rounded-md">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Exposure categories</div>
                    {(analytics.exposure_categories || []).length === 0
                      ? <div className="text-xs text-cream-dim italic">No exposure signals detected.</div>
                      : (analytics.exposure_categories || []).map((c, i) => (
                          <div key={i} className="flex justify-between py-1 border-b border-electric/10">
                            <span className="text-sm text-cream">{c.category}</span>
                            <span className="text-sm text-electric font-mono">{c.signal_count}</span>
                          </div>
                        ))}
                    <div className="mt-3 text-xs text-cream-dim">
                      Estimated savings if recommendations adopted: <span className="text-electric">{fmtMoney(analytics.estimated_savings)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STANDARDS MAPPING */}
            {standards && (
              <div>
                <SectionTitle icon={Building2} title="Standards Mapping" sub="non-certifying, decision-support orientation" />
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(standards.groups || []).map(g => (
                    <div key={g.vertical} className="glass p-4 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-display text-base text-cream">{g.vertical_label}</div>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-electric">{g.applicability}</span>
                      </div>
                      <ul className="text-xs text-cream-dim space-y-2">
                        {(g.standards || []).map(s => (
                          <li key={s.code}>
                            <strong className="text-cream">{s.code}</strong> · {s.name}
                            <div>{s.scope}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-cream-dim mt-3 italic">{standards.note}</div>
              </div>
            )}

            {/* DISCLAIMER */}
            <div className="glass p-3 rounded-md flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-300 mt-0.5" />
              <p className="text-xs text-cream-dim">
                AuditIQ findings are decision-support only and represent potential issues for management review.
                They do not constitute a finding of fact or a legal accusation.
              </p>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default AuditCrawler;
